// usersFirestoreService.ts
import { db } from './config';
import { cache } from '../utils/cacheManager';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  Timestamp,
  limit,
  startAfter,
  DocumentData,
  Query,
  QuerySnapshot,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  getCountFromServer
} from 'firebase/firestore';

export interface UserData {
  uid: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  dateOfBirth?: string;
  phone?: string;
  role: 'super_admin' | 'admin' | 'user' | 'moderator';
  isActive: boolean;
  emailVerified: boolean;
  provider: string[];
  lastLoginAt: Date | Timestamp;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  marketingEmails: boolean;
  termsAccepted: boolean;
  termsAcceptedAt: Date | Timestamp;
  profilePhoto?: string;
  lastSeen?: Date | Timestamp;
  loginCount?: number;
  browser?: string;
  device?: string;
  location?: string;
  ipAddress?: string;
}

export interface UserActivity {
  id?: string;
  userId: string;
  activityType: 'login' | 'logout' | 'page_view' | 'action' | 'error';
  pageUrl: string;
  pageTitle: string;
  timestamp: Date | Timestamp;
  metadata?: {
    browser?: string;
    device?: string;
    location?: string;
    ipAddress?: string;
    sessionDuration?: number;
  };
}

// Update usersCacheKeys to include user profile cache
export const usersCacheKeys = {
  usersAll: (includeAdmins: boolean = false) => includeAdmins ? 'users.all.includeAdmins' : 'users.all',
  stats: 'users.stats',
  growth: (period: string) => `users.growth.${period}`,
  userProfile: (userId: string) => `user.profile.${userId}` // Add this line
};



// Add these interfaces at the top of usersFirestoreService.ts (after UserData interface)

// Update UserPermissions interface to include custom sidebar visibility
export interface UserPermissions {
  // Existing section permissions...
  news: { view: boolean; create: boolean; edit: boolean; delete: boolean; publish: boolean };
  library: { view: boolean; create: boolean; edit: boolean; delete: boolean; manageMembers: boolean; manageBooks: boolean; manageAttendance: boolean };
  programs: { view: boolean; create: boolean; edit: boolean; delete: boolean; manageRegistrations: boolean };
  accounting: { view: boolean; create: boolean; edit: boolean; delete: boolean; approve: boolean };
  users: { view: boolean; create: boolean; edit: boolean; delete: boolean; changeRole: boolean; managePermissions: boolean };
  settings: { view: boolean; edit: boolean };
  
  // New analytics and reports sections
  analytics?: { view: boolean; export: boolean };
  reports?: { view: boolean; create: boolean; schedule: boolean };
  
  // Feature-specific permissions
  canExportData: boolean;
  canImportData: boolean;
  canViewAnalytics: boolean;
  canSendNotifications: boolean;
  canManageSystem: boolean;
  
  // Custom sidebar visibility (optional - for overriding section-based visibility)
  sidebarVisibility?: Record<string, boolean>; // Key: path, Value: visible (true/false)
}

export interface RolePermissionPreset {
  id: string;
  name: string;
  description: string;
  permissions: Partial<UserPermissions>;
}

// Add this interface at the top of usersFirestoreService.ts
export interface SidebarMenuItem {
  path: string;
  label: string;
  requiredSection?: string;
  requiredRole?: string;
  icon?: string; // Optional icon identifier
}

// Add this array of all sidebar menu items with their requirements
export const ALL_SIDEBAR_MENU_ITEMS: SidebarMenuItem[] = [

  { 
    path: '/admin/library', 
    label: 'Library', 
    requiredSection: 'library'
  },
  { 
    path: '/admin/news', 
    label: 'News Management', 
    requiredSection: 'news'
  },
  { 
    path: '/admin/programs', 
    label: 'Programs', 
    requiredSection: 'programs'
  },
  { 
    path: '/admin/users', 
    label: 'User Management', 
    requiredRole: 'admin'
  },
  { 
    path: '/admin/accounting', 
    label: 'Accounting', 
    requiredSection: 'accounting'
  },
  { 
    path: '/admin/settings', 
    label: 'Settings', 
    requiredSection: 'settings'
  },
  { 
    path: '/admin/support', 
    label: 'Support'
  },


];


// Update DEFAULT_PERMISSION_PRESETS in usersFirestoreService.ts
// Update DEFAULT_PERMISSION_PRESETS
export const DEFAULT_PERMISSION_PRESETS: Record<string, Partial<UserPermissions>> = {
  viewer: {
    // View only for all sections
    news: { view: true, create: false, edit: false, delete: false, publish: false },
    library: { view: true, create: false, edit: false, delete: false, manageMembers: false, manageBooks: false, manageAttendance: false },
    programs: { view: true, create: false, edit: false, delete: false, manageRegistrations: false },
    accounting: { view: true, create: false, edit: false, delete: false, approve: false },
    users: { view: false, create: false, edit: false, delete: false, changeRole: false, managePermissions: false },
    settings: { view: false, edit: false },
    // Feature-specific - viewer gets none
    canExportData: false,
    canImportData: false,
    canViewAnalytics: true,
    canSendNotifications: false,
    canManageSystem: false,
  },
  editor: {
    // Full access for all sections (except user management)
    news: { view: true, create: true, edit: true, delete: true, publish: true },
    library: { view: true, create: true, edit: true, delete: true, manageMembers: true, manageBooks: true, manageAttendance: true },
    programs: { view: true, create: true, edit: true, delete: true, manageRegistrations: true },
    accounting: { view: true, create: true, edit: true, delete: true, approve: true },
    users: { view: false, create: false, edit: false, delete: false, changeRole: false, managePermissions: false },
    settings: { view: true, edit: true },
    // Feature-specific - editor gets most features
    canExportData: true,
    canImportData: true,
    canViewAnalytics: true,
    canSendNotifications: true,
    canManageSystem: false,
  },
  // Add admin preset (same as editor but can view users)
  admin: {
    news: { view: true, create: true, edit: true, delete: true, publish: true },
    library: { view: true, create: true, edit: true, delete: true, manageMembers: true, manageBooks: true, manageAttendance: true },
    programs: { view: true, create: true, edit: true, delete: true, manageRegistrations: true },
    accounting: { view: true, create: true, edit: true, delete: true, approve: true },
    users: { view: true, create: false, edit: true, delete: false, changeRole: true, managePermissions: false },
    settings: { view: true, edit: true },
    canExportData: true,
    canImportData: true,
    canViewAnalytics: true,
    canSendNotifications: true,
    canManageSystem: false,
  }
};

// Update getDefaultPermissionsForRole function
export const getDefaultPermissionsForRole = (role: string): UserPermissions => {
  const defaultPermissions: UserPermissions = {
    news: { view: false, create: false, edit: false, delete: false, publish: false },
    library: { view: false, create: false, edit: false, delete: false, manageMembers: false, manageBooks: false, manageAttendance: false },
    programs: { view: false, create: false, edit: false, delete: false, manageRegistrations: false },
    accounting: { view: false, create: false, edit: false, delete: false, approve: false },
    users: { view: false, create: false, edit: false, delete: false, changeRole: false, managePermissions: false },
    settings: { view: false, edit: false },
    canExportData: false,
    canImportData: false,
    canViewAnalytics: false,
    canSendNotifications: false,
    canManageSystem: false,
  };

  switch (role) {
    case 'super_admin':
      // Super admin has all permissions
      return {
        news: { view: true, create: true, edit: true, delete: true, publish: true },
        library: { view: true, create: true, edit: true, delete: true, manageMembers: true, manageBooks: true, manageAttendance: true },
        programs: { view: true, create: true, edit: true, delete: true, manageRegistrations: true },
        accounting: { view: true, create: true, edit: true, delete: true, approve: true },
        users: { view: true, create: true, edit: true, delete: true, changeRole: true, managePermissions: true },
        settings: { view: true, edit: true },
        canExportData: true,
        canImportData: true,
        canViewAnalytics: true,
        canSendNotifications: true,
        canManageSystem: true,
      };
    case 'admin':
      return DEFAULT_PERMISSION_PRESETS.admin as UserPermissions;
    case 'moderator':
      return DEFAULT_PERMISSION_PRESETS.editor as UserPermissions;
    case 'user':
      return DEFAULT_PERMISSION_PRESETS.viewer as UserPermissions;
    default:
      return defaultPermissions;
  }
};

// Add function to toggle sidebar item visibility
export const toggleSidebarItemVisibility = async (
  userId: string,
  itemPath: string,
  visible: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const currentData = userDoc.data();
    const currentPermissions = currentData.permissions || getDefaultPermissionsForRole(currentData.role);
    
    // Update sidebar visibility
    const updatedPermissions = {
      ...currentPermissions,
      sidebarVisibility: {
        ...currentPermissions.sidebarVisibility,
        [itemPath]: visible
      }
    };
    
    await updateDoc(userRef, {
      permissions: updatedPermissions,
      updatedAt: new Date(),
    });
    // Invalidate relevant caches
    try {
      cache.invalidate(usersCacheKeys.usersAll(false));
      cache.invalidate(usersCacheKeys.usersAll(true));
      cache.invalidate(usersCacheKeys.stats);
      cache.invalidatePattern(/^users\.growth\./);
    } catch (e) {}

    return { success: true };
  } catch (error: any) {
    console.error('Error toggling sidebar item visibility:', error);
    return { success: false, error: error.message };
  }
};

// Add function to get sidebar visibility for a user
export const getUserSidebarVisibility = async (
  userId: string
): Promise<{ success: boolean; data: Record<string, boolean>; error?: string }> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, data: {}, error: 'User not found' };
    }
    
    const userData = userDoc.data();
    const permissions = userData.permissions || getDefaultPermissionsForRole(userData.role);
    
    return { 
      success: true, 
      data: permissions.sidebarVisibility || {} 
    };
  } catch (error: any) {
    console.error('Error getting sidebar visibility:', error);
    return { success: false, data: {}, error: error.message };
  }
};

// In usersFirestoreService.ts, add logging
export const updateSidebarVisibility = async (
  userId: string,
  visibility: Record<string, boolean>
): Promise<{ success: boolean; error?: string }> => {
  console.log(`🔄 Updating sidebar visibility for user ${userId}:`, visibility);
  try {
    const userRef = doc(db, 'users', userId);
    
    // Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      console.log('❌ User not found');
      return { success: false, error: 'User not found' };
    }
    
    const currentData = userDoc.data();
    console.log('📋 Current user data:', currentData);
    
    const currentPermissions = currentData.permissions || getDefaultPermissionsForRole(currentData.role);
    console.log('📋 Current permissions:', currentPermissions);
    
    // Update sidebar visibility
    const updatedPermissions = {
      ...currentPermissions,
      sidebarVisibility: {
        ...currentPermissions.sidebarVisibility,
        ...visibility
      }
    };
    
    console.log('✅ Updated permissions:', updatedPermissions);
    
    await updateDoc(userRef, {
      permissions: updatedPermissions,
      permissionPreset: 'custom',
      updatedAt: new Date(),
    });
    
    // Invalidate relevant caches
    try {
      console.log('🗑️ Invalidating caches...');
      cache.invalidate(usersCacheKeys.usersAll(false));
      cache.invalidate(usersCacheKeys.usersAll(true));
      cache.invalidate(usersCacheKeys.stats);
      cache.invalidate(usersCacheKeys.userProfile(userId));
      cache.invalidatePattern(/^users\.growth\./);
      console.log('✅ Caches invalidated');
    } catch (e) {
      console.error('❌ Error invalidating cache:', e);
    }

    return { success: true };
  } catch (error: any) {
    console.error('❌ Error updating sidebar visibility:', error);
    return { success: false, error: error.message };
  }
};
// Add a helper function to get simplified permission presets
export const getSimplifiedPermissionPresets = () => ({
  viewer: {
    name: 'Viewer',
    description: 'Can only view content, no editing rights',
    permissions: DEFAULT_PERMISSION_PRESETS.viewer
  },
  editor: {
    name: 'Editor',
    description: 'Full editing rights for all sections',
    permissions: DEFAULT_PERMISSION_PRESETS.editor
  }
});

// Update UserData interface to include permissions
export interface UserData {
  uid: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  dateOfBirth?: string;
  phone?: string;
  role: 'super_admin' | 'admin' | 'user' | 'moderator';
  isActive: boolean;
  emailVerified: boolean;
  provider: string[];
  lastLoginAt: Date | Timestamp;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  marketingEmails: boolean;
  termsAccepted: boolean;
  termsAcceptedAt: Date | Timestamp;
  profilePhoto?: string;
  lastSeen?: Date | Timestamp;
  loginCount?: number;
  browser?: string;
  device?: string;
  location?: string;
  ipAddress?: string;
  permissions?: UserPermissions; // Added permissions field
  permissionPreset?: string; // Reference to permission preset
}

// Add these functions to the service file:

// Get user with permissions
export const getUserWithPermissions = async (userId: string): Promise<{ 
  success: boolean; 
  data?: UserData; 
  error?: string 
}> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('uid', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'User not found' };
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    // Merge with default permissions if none exist
    const user = {
      uid: doc.id,
      ...data,
      permissions: data.permissions || getDefaultPermissionsForRole(data.role),
      lastLoginAt: data.lastLoginAt?.toDate() || data.lastLoginAt,
      createdAt: data.createdAt?.toDate() || data.createdAt,
      updatedAt: data.updatedAt?.toDate() || data.updatedAt,
      lastSeen: data.lastSeen?.toDate() || data.lastSeen,
      termsAcceptedAt: data.termsAcceptedAt?.toDate() || data.termsAcceptedAt,
    } as unknown as UserData;
    
    return { success: true, data: user };
  } catch (error: any) {
    console.error('Error fetching user with permissions:', error);
    return { success: false, error: error.message };
  }
};

// Get default permissions based on role

// Add this function to usersFirestoreService.ts

// Update user's specific section permissions
export const updateUserSectionPermission = async (
  userId: string,
  section: keyof UserPermissions,
  permissions: Partial<{
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    [key: string]: boolean;
  }>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const currentData = userDoc.data();
    const currentPermissions = currentData.permissions || getDefaultPermissionsForRole(currentData.role);
    
    // Update specific section permissions
    const updatedPermissions = {
      ...currentPermissions,
      [section]: {
        ...currentPermissions[section],
        ...permissions
      }
    };
    
    await updateDoc(userRef, {
      permissions: updatedPermissions,
      permissionPreset: 'custom', // Mark as custom since we're changing specific permissions
      updatedAt: new Date(),
    });
    // Invalidate relevant caches
    try {
      cache.invalidate(usersCacheKeys.usersAll(false));
      cache.invalidate(usersCacheKeys.usersAll(true));
      cache.invalidate(usersCacheKeys.stats);
      cache.invalidatePattern(/^users\.growth\./);
    } catch (e) {}

    return { success: true };
  } catch (error: any) {
    console.error('Error updating user section permissions:', error);
    return { success: false, error: error.message };
  }
};

// Toggle specific permission
export const toggleUserPermission = async (
  userId: string,
  section: keyof UserPermissions,
  permission: string,
  value: boolean
): Promise<{ success: boolean; error?: string }> => {
  return updateUserSectionPermission(userId, section, { [permission]: value });
};

// Update user permissions
export const updateUserPermissions = async (
  userId: string, 
  permissions: Partial<UserPermissions>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const currentData = userDoc.data();
    const currentPermissions = currentData.permissions || getDefaultPermissionsForRole(currentData.role);
    
    // Merge permissions
    const updatedPermissions = {
      ...currentPermissions,
      ...permissions,
      // Ensure nested objects are properly merged
      news: { ...currentPermissions.news, ...permissions.news },
      library: { ...currentPermissions.library, ...permissions.library },
      programs: { ...currentPermissions.programs, ...permissions.programs },
      accounting: { ...currentPermissions.accounting, ...permissions.accounting },
      users: { ...currentPermissions.users, ...permissions.users },
      settings: { ...currentPermissions.settings, ...permissions.settings },
    };
    
    await updateDoc(userRef, {
      permissions: updatedPermissions,
      updatedAt: new Date(),
    });
    // Invalidate relevant caches
    try {
      cache.invalidate(usersCacheKeys.usersAll(false));
      cache.invalidate(usersCacheKeys.usersAll(true));
      cache.invalidate(usersCacheKeys.stats);
      cache.invalidatePattern(/^users\.growth\./);
    } catch (e) {}

    return { success: true };
  } catch (error: any) {
    console.error('Error updating user permissions:', error);
    return { success: false, error: error.message };
  }
};

// Apply permission preset to user
export const applyPermissionPreset = async (
  userId: string, 
  presetId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const preset = DEFAULT_PERMISSION_PRESETS[presetId];
    if (!preset) {
      return { success: false, error: 'Invalid preset' };
    }
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      permissions: preset,
      permissionPreset: presetId,
      updatedAt: new Date(),
    });
    // Invalidate relevant caches
    try {
      cache.invalidate(usersCacheKeys.usersAll(false));
      cache.invalidate(usersCacheKeys.usersAll(true));
      cache.invalidate(usersCacheKeys.stats);
      cache.invalidatePattern(/^users\.growth\./);
    } catch (e) {}

    return { success: true };
  } catch (error: any) {
    console.error('Error applying permission preset:', error);
    return { success: false, error: error.message };
  }
};

// Update user role and permissions
export const updateUserRole = async (
  userId: string, 
  newRole: string,
  applyDefaultPermissions: boolean = true
): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    const updateData: any = {
      role: newRole,
      updatedAt: new Date(),
    };
    
    if (applyDefaultPermissions) {
      updateData.permissions = getDefaultPermissionsForRole(newRole);
      updateData.permissionPreset = null; // Clear preset when role changes
    }
    
    await updateDoc(userRef, updateData);
    // Invalidate relevant caches due to role/permission change
    try {
      cache.invalidate(usersCacheKeys.usersAll(false));
      cache.invalidate(usersCacheKeys.usersAll(true));
      cache.invalidate(usersCacheKeys.stats);
      cache.invalidatePattern(/^users\.growth\./);
    } catch (e) {}

    return { success: true };
  } catch (error: any) {
    console.error('Error updating user role:', error);
    return { success: false, error: error.message };
  }
};

// Get all admin activities (for super_admin only)
export const getAdminActivities = async (
  limitCount: number = 100,
  startAfter?: any
): Promise<{ 
  success: boolean; 
  data: UserActivity[]; 
  lastVisible?: any;
  error?: string 
}> => {
  try {
    const activitiesRef = collection(db, 'adminActivities');
    let q: Query<DocumentData>;
    
    if (startAfter) {
      q = query(
        activitiesRef,
        orderBy('timestamp', 'desc'),
        startAfter(startAfter),
        limit(limitCount)
      );
    } else {
      q = query(
        activitiesRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    }
    
    const querySnapshot = await getDocs(q);
    const activities: UserActivity[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      activities.push({
        id: doc.id,
        ...data,
        timestamp: data.timestamp?.toDate() || data.timestamp,
      } as UserActivity);
    });
    
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    
    return { 
      success: true, 
      data: activities,
      lastVisible
    };
  } catch (error: any) {
    console.error('Error fetching admin activities:', error);
    return { success: false, data: [], error: error.message };
  }
};

// Log admin activity
export const logAdminActivity = async (
  userId: string,
  activityType: UserActivity['activityType'],
  action: string,
  details?: any
): Promise<{ success: boolean; error?: string }> => {
  try {
    const activitiesRef = collection(db, 'adminActivities');
    
    await addDoc(activitiesRef, {
      userId,
      activityType,
      action,
      details: details || null,
      timestamp: new Date(),
      metadata: {
        browser: navigator.userAgent,
        timestamp: Date.now(),
      }
    });
    
    return { success: true };
  } catch (error: any) {
    console.error('Error logging admin activity:', error);
    return { success: false, error: error.message };
  }
};
// Add these helper functions at the top of the service file

// Normalize Firestore data for caching
export const normalizeUserForCache = (user: UserData): any => {
  return {
    ...user,
    lastLoginAt: user.lastLoginAt instanceof Date ? user.lastLoginAt.toISOString() : 
                 user.lastLoginAt?.toDate?.()?.toISOString() || null,
    createdAt: user.createdAt instanceof Date ? user.createdAt.toISOString() : 
               user.createdAt?.toDate?.()?.toISOString() || null,
    updatedAt: user.updatedAt instanceof Date ? user.updatedAt.toISOString() : 
               user.updatedAt?.toDate?.()?.toISOString() || null,
    lastSeen: user.lastSeen instanceof Date ? user.lastSeen.toISOString() : 
              user.lastSeen?.toDate?.()?.toISOString() || null,
    termsAcceptedAt: user.termsAcceptedAt instanceof Date ? user.termsAcceptedAt.toISOString() : 
                     user.termsAcceptedAt?.toDate?.()?.toISOString() || null,
  };
};

// Restore dates from cached data
export const restoreUserFromCache = (data: any): UserData => {
  return {
    ...data,
    lastLoginAt: data.lastLoginAt ? new Date(data.lastLoginAt) : null,
    createdAt: data.createdAt ? new Date(data.createdAt) : null,
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
    lastSeen: data.lastSeen ? new Date(data.lastSeen) : null,
    termsAcceptedAt: data.termsAcceptedAt ? new Date(data.termsAcceptedAt) : null,
  } as UserData;
};

// Update getAllUsers to include all roles or add a parameter
export const getAllUsers = async (includeAdmins: boolean = false): Promise<{ success: boolean; data: UserData[]; error?: string }> => {
  const key = usersCacheKeys.usersAll(includeAdmins);
  const cached = await cache.get(key, async () => {
    try {
      const usersRef = collection(db, 'users');
      let q;
      
      if (includeAdmins) {
        q = query(
          usersRef,
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          usersRef,
          where('role', 'in', ['user', 'moderator']),
          orderBy('createdAt', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      const users: UserData[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const user = {
          uid: doc.id,
          ...data,
          lastLoginAt: data.lastLoginAt?.toDate() || data.lastLoginAt,
          createdAt: data.createdAt?.toDate() || data.createdAt,
          updatedAt: data.updatedAt?.toDate() || data.updatedAt,
          lastSeen: data.lastSeen?.toDate() || data.lastSeen,
          termsAcceptedAt: data.termsAcceptedAt?.toDate() || data.termsAcceptedAt,
        } as unknown as UserData;
        
        users.push(user);
      });
      
      // Normalize dates before caching to reduce serialized size
      const normalized = users.map(u => normalizeUserForCache(u));
      return { success: true, data: normalized };
    } catch (error: any) {
      console.error('Error fetching users:', error);
      return { success: false, data: [], error: error.message };
    }
  }, 10 * 60 * 1000);

  // Restore dates before returning to callers
  if (!cached || !cached.success) return cached as any;
  try {
    const restored = (cached.data || []).map((d: any) => restoreUserFromCache(d));
    return { success: true, data: restored };
  } catch (e) {
    return { success: true, data: cached.data } as any;
  }
};


export const getUserActivities = async (userId?: string): Promise<{ success: boolean; data: UserActivity[]; error?: string }> => {
  try {
    const activitiesRef = collection(db, 'userActivities');
    let q: Query<DocumentData>;
    
    if (userId) {
      q = query(
        activitiesRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
    } else {
      q = query(
        activitiesRef,
        orderBy('timestamp', 'desc'),
        limit(1000)
      );
    }
    
    const querySnapshot = await getDocs(q);
    const activities: UserActivity[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      activities.push({
        id: doc.id,
        ...data,
        timestamp: data.timestamp?.toDate() || data.timestamp,
      } as UserActivity);
    });
    
    return { success: true, data: activities };
  } catch (error: any) {
    console.error('Error fetching user activities:', error);
    return { success: false, data: [], error: error.message };
  }
};

// Get user statistics
export const getUserStatistics = async (): Promise<{ 
  success: boolean; 
  data: { 
    totalUsers: number;
    activeToday: number;
    activeThisWeek: number;
    activeThisMonth: number;
    newUsersToday: number;
    newUsersThisWeek: number;
    newUsersThisMonth: number;
    maleCount: number;
    femaleCount: number;
    otherGenderCount: number;
    verifiedCount: number;
    topBrowsers: Array<{ browser: string; count: number }>;
    topDevices: Array<{ device: string; count: number }>;
    topLocations: Array<{ location: string; count: number }>;
    peakHours: Array<{ hour: number; count: number }>;
  }; 
  error?: string 
}> => {
  return cache.get(usersCacheKeys.stats, async () => {
    try {
      // Get all users (excluding admin/super_admin)
      const usersResponse = await getAllUsers();
      if (!usersResponse.success) {
        return { 
          success: false, 
          data: {
            totalUsers: 0,
            activeToday: 0,
            activeThisWeek: 0,
            activeThisMonth: 0,
            newUsersToday: 0,
            newUsersThisWeek: 0,
            newUsersThisMonth: 0,
            maleCount: 0,
            femaleCount: 0,
            otherGenderCount: 0,
            verifiedCount: 0,
            topBrowsers: [],
            topDevices: [],
            topLocations: [],
            peakHours: []
          }, 
          error: usersResponse.error 
        };
      }
      
      const users = usersResponse.data as unknown as UserData[];
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      
      // Calculate statistics
      const statistics = {
        totalUsers: users.length,
        activeToday: users.filter(user => {
          const lastLogin = user.lastLoginAt instanceof Date ? user.lastLoginAt : user.lastLoginAt?.toDate?.();
          return lastLogin && lastLogin >= today;
        }).length,
        activeThisWeek: users.filter(user => {
          const lastLogin = user.lastLoginAt instanceof Date ? user.lastLoginAt : user.lastLoginAt?.toDate?.();
          return lastLogin && lastLogin >= weekAgo;
        }).length,
        activeThisMonth: users.filter(user => {
          const lastLogin = user.lastLoginAt instanceof Date ? user.lastLoginAt : user.lastLoginAt?.toDate?.();
          return lastLogin && lastLogin >= monthAgo;
        }).length,
        newUsersToday: users.filter(user => {
          const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
          return createdAt && createdAt >= today;
        }).length,
        newUsersThisWeek: users.filter(user => {
          const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
          return createdAt && createdAt >= weekAgo;
        }).length,
        newUsersThisMonth: users.filter(user => {
          const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
          return createdAt && createdAt >= monthAgo;
        }).length,
        maleCount: users.filter(user => user.gender === 'male').length,
        femaleCount: users.filter(user => user.gender === 'female').length,
        otherGenderCount: users.filter(user => !['male', 'female'].includes(user.gender || '')).length,
        verifiedCount: users.filter(user => user.emailVerified).length,
        topBrowsers: [] as Array<{ browser: string; count: number }> ,
        topDevices: [] as Array<{ device: string; count: number }>,
        topLocations: [] as Array<{ location: string; count: number }>,
        peakHours: [] as Array<{ hour: number; count: number }>
      };
      
      // Get activities for browser/device/location analysis
      const activitiesResponse = await getUserActivities();
      if (activitiesResponse.success && activitiesResponse.data.length > 0) {
        const activities = activitiesResponse.data;
        
        // Analyze browsers
        const browserMap = new Map<string, number>();
        activities.forEach(activity => {
          const browser = activity.metadata?.browser || 'Unknown';
          browserMap.set(browser, (browserMap.get(browser) || 0) + 1);
        });
        statistics.topBrowsers = Array.from(browserMap.entries())
          .map(([browser, count]) => ({ browser, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        
        // Analyze devices
        const deviceMap = new Map<string, number>();
        activities.forEach(activity => {
          const device = activity.metadata?.device || 'Unknown';
          deviceMap.set(device, (deviceMap.get(device) || 0) + 1);
        });
        statistics.topDevices = Array.from(deviceMap.entries())
          .map(([device, count]) => ({ device, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        
        // Analyze locations
        const locationMap = new Map<string, number>();
        activities.forEach(activity => {
          const location = activity.metadata?.location || 'Unknown';
          locationMap.set(location, (locationMap.get(location) || 0) + 1);
        });
        statistics.topLocations = Array.from(locationMap.entries())
          .map(([location, count]) => ({ location, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        
        // Analyze peak hours
        const hourMap = new Map<number, number>();
        activities.forEach(activity => {
          const timestamp = activity.timestamp instanceof Date ? activity.timestamp : activity.timestamp?.toDate?.();
          if (timestamp) {
            const hour = timestamp.getHours();
            hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
          }
        });
        statistics.peakHours = Array.from({ length: 24 }, (_, i) => ({
          hour: i,
          count: hourMap.get(i) || 0
        }));
      }
      
      return { success: true, data: statistics };
    } catch (error: any) {
      console.error('Error calculating user statistics:', error);
      return { 
        success: false, 
        data: {
          totalUsers: 0,
          activeToday: 0,
          activeThisWeek: 0,
          activeThisMonth: 0,
          newUsersToday: 0,
          newUsersThisWeek: 0,
          newUsersThisMonth: 0,
          maleCount: 0,
          femaleCount: 0,
          otherGenderCount: 0,
          verifiedCount: 0,
          topBrowsers: [],
          topDevices: [],
          topLocations: [],
          peakHours: []
        }, 
        error: error.message 
      };
    }
  }, 10 * 60 * 1000);
};

// Get paginated users
export const getPaginatedUsers = async (
  page: number, 
  pageSize: number = 20
): Promise<{ 
  success: boolean; 
  data: UserData[]; 
  total: number;
  hasMore: boolean;
  error?: string 
}> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('role', 'in', ['user', 'moderator']),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );
    
    const querySnapshot = await getDocs(q);
    const users: UserData[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        uid: doc.id,
        ...data,
        lastLoginAt: data.lastLoginAt?.toDate() || data.lastLoginAt,
        createdAt: data.createdAt?.toDate() || data.createdAt,
        updatedAt: data.updatedAt?.toDate() || data.updatedAt,
        lastSeen: data.lastSeen?.toDate() || data.lastSeen,
      } as unknown as UserData);
    });
    
    // For total count, we need a separate query
    const countQuery = query(
      usersRef,
      where('role', 'in', ['user', 'moderator'])
    );
    const countSnapshot = await getCountFromServer(countQuery);
    const total = countSnapshot.data().count;
    
    return { 
      success: true, 
      data: users, 
      total,
      hasMore: total > page * pageSize 
    };
  } catch (error: any) {
    console.error('Error fetching paginated users:', error);
    return { 
      success: false, 
      data: [], 
      total: 0,
      hasMore: false,
      error: error.message 
    };
  }
};



// Get user growth data for charts
export const getUserGrowthData = async (period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'monthly') => {
  return cache.get(usersCacheKeys.growth(period), async () => {
    try {
      const usersResponse = await getAllUsers();
      if (!usersResponse.success) {
        return { success: false, data: [], error: usersResponse.error };
      }
      
      const users = usersResponse.data;
      const now = new Date();
      
      // Process based on period
      let data: Array<{ period: string; count: number; newUsers: number; activeUsers: number }> = [];
      
      switch (period) {
      case 'daily':
        // Last 30 days
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          const nextDate = new Date(date);
          nextDate.setDate(nextDate.getDate() + 1);
          
          const periodUsers = users.filter(user => {
            const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
            return createdAt && createdAt >= date && createdAt < nextDate;
          });
          
          const activeUsers = users.filter(user => {
            const lastLogin = user.lastLoginAt instanceof Date ? user.lastLoginAt : user.lastLoginAt?.toDate?.();
            return lastLogin && lastLogin >= date && lastLogin < nextDate;
          }).length;
          
          data.push({
            period: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            count: users.filter(user => {
              const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
              return createdAt && createdAt < nextDate;
            }).length,
            newUsers: periodUsers.length,
            activeUsers
          });
        }
        break;
        
      case 'weekly':
        // Last 12 weeks
        for (let i = 11; i >= 0; i--) {
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() - (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 7);
          
          const weekUsers = users.filter(user => {
            const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
            return createdAt && createdAt >= weekStart && createdAt < weekEnd;
          });
          
          const activeUsers = users.filter(user => {
            const lastLogin = user.lastLoginAt instanceof Date ? user.lastLoginAt : user.lastLoginAt?.toDate?.();
            return lastLogin && lastLogin >= weekStart && lastLogin < weekEnd;
          }).length;
          
          const weekLabel = `Week ${i + 1}`;
          data.push({
            period: weekLabel,
            count: users.filter(user => {
              const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
              return createdAt && createdAt < weekEnd;
            }).length,
            newUsers: weekUsers.length,
            activeUsers
          });
        }
        break;
        
      case 'monthly':
        // Last 12 months
        for (let i = 11; i >= 0; i--) {
          const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
          
          const monthUsers = users.filter(user => {
            const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
            return createdAt && createdAt >= month && createdAt < nextMonth;
          });
          
          const activeUsers = users.filter(user => {
            const lastLogin = user.lastLoginAt instanceof Date ? user.lastLoginAt : user.lastLoginAt?.toDate?.();
            return lastLogin && lastLogin >= month && lastLogin < nextMonth;
          }).length;
          
          data.push({
            period: month.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
            count: users.filter(user => {
              const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
              return createdAt && createdAt < nextMonth;
            }).length,
            newUsers: monthUsers.length,
            activeUsers
          });
        }
        break;
        
      case 'yearly':
        // Last 5 years
        for (let i = 4; i >= 0; i--) {
          const year = new Date(now.getFullYear() - i, 0, 1);
          const nextYear = new Date(year.getFullYear() + 1, 0, 1);
          
          const yearUsers = users.filter(user => {
            const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
            return createdAt && createdAt >= year && createdAt < nextYear;
          });
          
          const activeUsers = users.filter(user => {
            const lastLogin = user.lastLoginAt instanceof Date ? user.lastLoginAt : user.lastLoginAt?.toDate?.();
            return lastLogin && lastLogin >= year && lastLogin < nextYear;
          }).length;
          
          data.push({
            period: year.getFullYear().toString(),
            count: users.filter(user => {
              const createdAt = user.createdAt instanceof Date ? user.createdAt : user.createdAt?.toDate?.();
              return createdAt && createdAt < nextYear;
            }).length,
            newUsers: yearUsers.length,
            activeUsers
          });
        }
        break;
    }
    
      return { success: true, data };
    } catch (error: any) {
      console.error('Error getting user growth data:', error);
      return { success: false, data: [], error: error.message };
    }
  }, 10 * 60 * 1000);
};
